import { formatDanishLongDate } from "@/util/date";
import { TeachingEvidenceListModel } from "../models/teachingDocument";
import ClePagination from "./ClePagination";
import CleTable, { type CleTableColumn } from "./CleTable";
import EvidenceStatusBadge from "./EvidenceStatusBadge";
import { formatLessons } from "../utils/lessonLimits";

type TeachingsTableProps = {
  isError?: boolean;
  isLoading?: boolean;
  onTeachingClick?: (teachingId: string) => void;
  pagination?: {
    isDisabled?: boolean;
    onPageChange: (page: number) => void;
    page: number;
    total: number;
    totalPages: number;
  };
  showStatusColumn?: boolean;
  teachings: TeachingEvidenceListModel[];
};

const TeachingsTable = ({
  isError = false,
  isLoading = false,
  onTeachingClick,
  pagination,
  showStatusColumn = true,
  teachings,
}: TeachingsTableProps) => {
  const columns: CleTableColumn<TeachingEvidenceListModel>[] = [
    {
      id: "fileName",
      header: "Dokument",
      title: (teaching) => teaching.fileName ?? "",
      cell: (teaching) => teaching.fileName ?? "-",
    },
    {
      id: "createdAt",
      header: "Oprettet",
      cell: (teaching) => formatDanishLongDate(teaching.createdAt),
    },
    {
      id: "lessons",
      header: "Lektioner",
      className: "text-end",
      cell: (teaching) => formatLessons(teaching.numberOfLessons),
    },
    ...(showStatusColumn
      ? [
          {
            id: "status",
            header: "Status",
            cell: (teaching: TeachingEvidenceListModel) => (
              <EvidenceStatusBadge
                reported={teaching.isReported}
                status={teaching.status}
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <CleTable
        columns={columns}
        data={teachings}
        getRowId={(teaching) => teaching.id}
        emptyDescription="Når du opretter undervisning, vises dokumentationen her i oversigten."
        emptyTitle="Ingen undervisning oprettet endnu"
        errorMessage="Undervisning kunne ikke indlæses lige nu."
        isError={isError}
        isLoading={isLoading}
        rowAction={
          onTeachingClick
            ? {
                onClick: (id) => onTeachingClick(id),
              }
            : undefined
        }
      />
      {pagination != null && teachings.length > 0 ? (
        <ClePagination
          ariaLabel="Undervisning pagination"
          isDisabled={pagination.isDisabled}
          itemLabel="undervisning"
          onPageChange={pagination.onPageChange}
          page={pagination.page}
          total={pagination.total}
          totalPages={pagination.totalPages}
        />
      ) : null}
    </>
  );
};

export default TeachingsTable;
